import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Alert,
  Collapse,
} from "@mui/material";
import {
  AccessTime,
  Edit,
  Search,
  Close,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
} from "@mui/icons-material";
import CustomInput from "./CustomInput";

const TimezoneConfig = React.memo(({ 
  timezone, 
  onTimezoneChange, 
  timezones, 
  timezonesLoaded,
  currentTimezone,
  isEditing,
  setIsEditing,
  saving,
  domainId,
  onTimezoneSave
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [isHovering, setIsHovering] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showTimezoneList, setShowTimezoneList] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const getCurrentTime = (tz) => {
    try {
      return new Date().toLocaleString('en-US', { 
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      return 'Invalid timezone';
    }
  };

  const getTimezoneOffset = (tz) => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'shortOffset'
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      return offsetPart ? offsetPart.value : '';
    } catch (e) {
      return '';
    }
  };

  const formatTimezoneForDisplay = (tz) => {
    if (!tz) return '';
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      return `${city} (${getTimezoneOffset(tz)})`;
    }
    return tz;
  };

  const formatTimezoneForMobile = (tz) => {
    if (!tz) return '';
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      return isMobile ? city : `${city} (${getTimezoneOffset(tz)})`;
    }
    return tz;
  };

  const filteredTimezones = useMemo(() => {
    if (!searchInput.trim()) return timezones;
    
    const searchTerm = searchInput.toLowerCase();
    return timezones.filter(tz => 
      tz.toLowerCase().includes(searchTerm) ||
      tz.toLowerCase().replace(/_/g, ' ').includes(searchTerm) ||
      formatTimezoneForDisplay(tz).toLowerCase().includes(searchTerm)
    );
  }, [timezones, searchInput]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value.trim() && !showTimezoneList) {
      setShowTimezoneList(true);
    }
  };

  const handleSearchFocus = () => {
    setShowTimezoneList(true);
  };

  const handleTimezoneSelect = (tz) => {
    onTimezoneChange(tz);
    setSearchInput(tz);
    setShowTimezoneList(false);
    
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && filteredTimezones.length > 0) {
      handleTimezoneSelect(filteredTimezones[0]);
    }
    
    if (e.key === 'Escape') {
      setShowTimezoneList(false);
      searchInputRef.current?.blur();
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setShowTimezoneList(true);
    searchInputRef.current?.focus();
  };

  const handleSaveTimezone = async () => {
    if (!timezone || !domainId) return;
    
    setSavingTimezone(true);
    try {
      if (onTimezoneSave) {
        await onTimezoneSave(timezone);
      }
      setIsEditing(false);
      setSearchInput('');
      setShowTimezoneList(false);
    } catch (error) {
      console.error("Failed to save timezone:", error);
    } finally {
      setSavingTimezone(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listContainerRef.current &&
        !listContainerRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowTimezoneList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showTimezoneList) {
        setShowTimezoneList(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTimezoneList]);

  useEffect(() => {
    if (showTimezoneList && searchInput.trim() && filteredTimezones.length === 0) {
      const timer = setTimeout(() => {
        setShowTimezoneList(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showTimezoneList, searchInput, filteredTimezones.length]);

  if (isEditing) {
    return (
      <Box 
        className="timezone-edit-container"
        ref={listContainerRef}
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          backgroundColor: '#f8fafa', 
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          mb: 3,
          position: 'relative'
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <AccessTime sx={{ color: '#219297', fontSize: { xs: 16, sm: 18 } }} />
            <Typography 
              variant="subtitle2" 
              fontWeight="600" 
              sx={{ 
                color: '#333', 
                fontSize: { xs: '0.85rem', sm: '0.9rem' } 
              }}
            >
              Select Timezone
            </Typography>
          </Box>
          <Chip
            label="Required"
            size="small"
            sx={{ 
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              backgroundColor: '#21929715',
              color: '#219297',
              fontWeight: 500
            }}
          />
        </Box>
        
        <Box sx={{ mb: 2, position: 'relative' }}>
          <Box sx={{ position: 'relative' }}>
            <CustomInput
              ref={searchInputRef}
              value={searchInput}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search timezones..."
              startIcon={<Search fontSize="small" />}
              style={{ marginBottom: '8px' }}
              endAdornment={
                searchInput ? (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ p: 0.5 }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => setShowTimezoneList(!showTimezoneList)}
                    sx={{ p: 0.5 }}
                  >
                    {showTimezoneList ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                )
              }
              autoFocus={!isMobile}
            />
            
            {searchInput && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  ml: 1,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {filteredTimezones.length} {filteredTimezones.length === 1 ? 'timezone' : 'timezones'} found
              </Typography>
            )}
            
            {showTimezoneList && (
              <Paper 
                ref={dropdownRef}
                sx={{ 
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: { xs: 200, sm: 250 }, 
                  overflow: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  zIndex: 1300,
                  mt: 0.5
                }}
              >
                {!timezonesLoaded ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#666', mb: 1 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' }
                      }}
                    >
                      Loading timezones...
                    </Typography>
                  </Box>
                ) : filteredTimezones.length > 0 ? (
                  <List sx={{ py: 0 }}>
                    {filteredTimezones.map((tz) => (
                      <ListItem
                        key={tz}
                        button
                        onClick={() => handleTimezoneSelect(tz)}
                        selected={timezone === tz}
                        sx={{
                          borderBottom: '1px solid #f0f0f0',
                          p: { xs: 1.5, sm: 2 },
                          '&:hover': {
                            backgroundColor: '#f0f7fa',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#21929710',
                            borderLeft: '3px solid #219297',
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ width: '100%' }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500, 
                                    color: '#333',
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                    mb: { xs: 0.5, sm: 0 }
                                  }}
                                >
                                  {formatTimezoneForMobile(tz)}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#666',
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                }}
                                >
                                  {getCurrentTime(tz)}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#999', 
                                  fontFamily: 'monospace', 
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                  display: 'block',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {tz}
                              </Typography>
                            </Box>
                          }
                        />
                        {timezone === tz && (
                          <CheckCircle sx={{ 
                            color: '#219297', 
                            fontSize: { xs: 14, sm: 16 },
                            ml: 1
                          }} />
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Search sx={{ 
                      fontSize: { xs: 24, sm: 32 }, 
                      color: '#ccc', 
                      mb: 1 
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#999',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' }
                      }}
                    >
                      No timezones found matching "{searchInput}"
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#999', 
                        display: 'block', 
                        mt: 1,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                    }}
                    >
                      Try a different search term
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
          
          <Box sx={{ 
            mt: 2,
            p: { xs: 1, sm: 1.5 },
            backgroundColor: '#f0f7fa',
            borderRadius: '8px',
            border: '1px solid #e0f0f0',
            position: 'relative',
            zIndex: 1
          }}>
            <Typography 
              variant="caption" 
              fontWeight="500"
              sx={{ 
                color: '#219297', 
                display: 'block',
                mb: 0.5,
                fontSize: { xs: '0.75rem', sm: '0.8rem' }
            }}
            >
              Selected Timezone:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace', 
                fontWeight: 600, 
                color: '#219297',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                wordBreak: 'break-word'
              }}
            >
              {timezone}
            </Typography>
            {timezone && timezone !== currentTimezone && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  display: 'block', 
                  mt: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                Current: {currentTimezone} → New: {timezone}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Box 
          sx={{ 
            position: isMobile ? 'sticky' : 'static',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#f8fafa',
            pt: 2,
            mt: 2,
            borderTop: isMobile ? '1px solid #e0e0e0' : 'none',
            zIndex: 10
          }}
        >
          <Box 
            display="flex" 
            gap={1} 
            justifyContent="flex-end" 
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Button
              size={isMobile ? "medium" : "small"}
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => {
                setIsEditing(false);
                onTimezoneChange(currentTimezone);
                setSearchInput('');
                setShowTimezoneList(false);
              }}
              disabled={saving || savingTimezone}
              sx={{ 
                fontSize: { xs: '0.85rem', sm: '0.75rem' },
                textTransform: 'none',
                fontWeight: 500,
                color: '#666',
                px: { xs: 2, sm: 2 },
                py: { xs: 1, sm: 0.5 }
              }}
            >
              Cancel
            </Button>
            <Button
              size={isMobile ? "medium" : "small"}
              variant="contained"
              fullWidth={isMobile}
              onClick={handleSaveTimezone}
              disabled={savingTimezone || !timezonesLoaded || !timezone || timezone === currentTimezone}
              startIcon={savingTimezone ? <CircularProgress size={isMobile ? 18 : 16} /> : null}
              sx={{ 
                fontSize: { xs: '0.85rem', sm: '0.75rem' },
                textTransform: 'none',
                fontWeight: 500,
                backgroundColor: '#219297',
                px: { xs: 2, sm: 2 },
                py: { xs: 1, sm: 0.5 },
                color: "#ffffff",
                '&:hover': {
                  backgroundColor: '#197f85',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999'
                }
              }}
            >
              {savingTimezone ? 'Saving...' : 'Save Timezone'}
            </Button>
          </Box>
          
          {isMobile && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#666', 
                display: 'block', 
                textAlign: 'center',
                mt: 1,
                fontSize: '0.7rem'
              }}
            >
              Tap search box to see timezones, tap one to select
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
  
  return (
    <Box
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => !isMobile && setIsHovering(false)}
      onClick={() => setIsEditing(true)}
      sx={{
        p: { xs: 1.5, sm: 2 },
        backgroundColor: isHovering ? '#f0f7fa' : '#f8fafa',
        borderRadius: '10px',
        border: `1px solid ${isHovering ? '#219297' : '#e0e0e0'}`,
        mb: 3,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(33, 146, 151, 0.1)',
          '& .timezone-edit-icon': {
            opacity: 1,
            transform: 'translateX(0)'
          }
        },
        '&:active': isMobile && {
          backgroundColor: '#e8f3f4',
          transform: 'scale(0.99)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: '#219297',
          borderTopLeftRadius: '10px',
          borderBottomLeftRadius: '10px'
        }}
      />
      
      <Box display="flex" alignItems="center" gap={2} sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: '8px',
            backgroundColor: '#21929715',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <AccessTime sx={{ 
            color: '#219297', 
            fontSize: { xs: 16, sm: 18 } 
          }} />
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5} flexWrap="wrap">
            <Typography 
              variant="subtitle2" 
              fontWeight="600" 
              sx={{ 
                color: '#333', 
                fontSize: { xs: '0.85rem', sm: '0.9rem' } 
              }}
            >
              Timezone
            </Typography>
            <Box 
              className="timezone-edit-icon"
              sx={{ 
                opacity: isHovering || isMobile ? 1 : 0,
                transform: (isHovering || isMobile) ? 'translateX(0)' : 'translateX(10px)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#219297', 
                  fontWeight: 500,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {isMobile ? 'Tap to edit' : 'Edit'}
              </Typography>
              <Edit sx={{ 
                color: '#219297', 
                fontSize: { xs: 14, sm: 16 } 
              }} />
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#219297', 
                fontWeight: 600, 
                fontFamily: 'monospace',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                wordBreak: 'break-word'
              }}
            >
              {isMobile && timezone.length > 20 
                ? timezone.split('/').pop()?.replace(/_/g, ' ') || timezone
                : timezone}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: (isHovering || isMobile) ? '#219297' : '#666'
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (isHovering || isMobile) ? '#219297' : '#666',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' }
                }}
              >
                {getCurrentTime(timezone)}
              </Typography>
            </Box>
            
            <Chip
              label={getTimezoneOffset(timezone) || 'UTC'}
              size="small"
              sx={{ 
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                height: { xs: '16px', sm: '18px' },
                backgroundColor: '#e0f7fa',
                color: '#219297',
                fontWeight: 500
              }}
            />
          </Box>
          
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666', 
              mt: 0.5, 
              display: 'block', 
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}
          >
            {isMobile ? 'Tap to change timezone' : 'Click to change timezone for scheduling'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default TimezoneConfig;